import React from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Key, Home, Building, MapPin, Smartphone, Lock } from 'lucide-react';

export default function EntryInfo({ entryInfo, onEntryInfoChange, highlightDetails = false }) {
  const entryTypes = [
    { 
      value: 'home', 
      label: 'Will be home', 
      icon: Home,
      description: 'I will be present during cleaning'
    },
    { 
      value: 'permission', 
      label: 'Permission from front desk', 
      icon: Building,
      description: 'Front desk will provide access'
    },
    { 
      value: 'hiding_spot', 
      label: 'Key hiding spot', 
      icon: MapPin,
      description: 'Key is hidden somewhere'
    },
    { 
      value: 'remote', 
      label: 'Will open remotely', 
      icon: Smartphone,
      description: 'I will unlock remotely'
    },
    { 
      value: 'lockbox', 
      label: 'Lockbox/Key code', 
      icon: Lock,
      description: 'Access via lockbox or code'
    }
  ];

  const requiresNotes = ['hiding_spot', 'lockbox'];
  const showNotesField = requiresNotes.includes(entryInfo.type);

  const selectEntryType = (type) => {
    onEntryInfoChange({ ...entryInfo, type });
  };

  return (
    <div className="">
      <div className="space-y-6">
        <div className="text-center">
          <div className="feature-badge gold mx-auto">
            <Key className="w-8 h-8 u-ink-800" size={30} />
          </div>
          <h2
            className="section-heading"
            style={{ textAlign: 'center', marginTop: '16px', marginBottom: '16px' }}
          >
            Entry Information
          </h2>
        </div>

        <div>
          <div className="space-y-6">
            <div>
              <div className="entry-options-grid">
                {entryTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = entryInfo.type === type.value;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => selectEntryType(type.value)}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 btn-ghost entry-type ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <Icon className="w-5 h-5 u-teal-600" />
                        <span className="font-medium text-sm u-ink-800">
                          {type.label}
                        </span>
                      </div>
                      <p className="text-xs opacity-75 text-left u-ink-800">
                        {type.description}
                      </p>
                      <div className="entry-selected-slot">
                        <span className={`svc-chip ${isSelected ? '' : 'hidden-keep-space'}`}>Selected</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {showNotesField && (
              <div className={`entry-details-block ${highlightDetails ? 'highlight-entry-details' : ''}`}>
                <Label className="font-medium u-ink-800 entry-details-label">
                  {entryInfo.type === 'hiding_spot' ? 'Key Hiding Spot Details *' : 'Lockbox/Key Code Details *'}
                </Label>
                <Textarea
                  value={entryInfo.notes || ''}
                  onChange={(e) => onEntryInfoChange({ ...entryInfo, notes: e.target.value })}
                  className="mt-2 entry-details-textarea"
                  placeholder={
                    entryInfo.type === 'hiding_spot'
                      ? 'Please describe where the key is hidden (e.g., under flower pot by front door, inside fake rock near mailbox)'
                      : 'Please provide lockbox location and code or key pickup instructions'
                  }
                  required
                />
              </div>
            )}

            {entryInfo.type && !showNotesField && (
              <div className="alert-banner entry-alert">
                <div className="flex items-start gap-2">
                  <Key className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium u-ink-800">Entry Method Selected</p>
                    <p className="text-sm u-ink-800">
                      {entryTypes.find((t) => t.value === entryInfo.type)?.label}
                    </p>
                    {entryInfo.type === 'home' && (
                      <p className="text-xs mt-1 u-ink-800">
                        Our team will ring the doorbell upon arrival.
                      </p>
                    )}
                    {entryInfo.type === 'permission' && (
                      <p className="text-xs mt-1 u-ink-800">
                        Please inform your front desk about our scheduled visit.
                      </p>
                    )}
                    {entryInfo.type === 'remote' && (
                      <p className="text-xs mt-1 u-ink-800">
                        Our team will call you 5-10 minutes before arrival.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
